import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import {
  ContentItem,
  ContentListProviders,
  UIElementContent,
  UIElementStyle,
  UniverseSortingValue
} from '@smart-tv/typings/content';

import useInterval from '@smart-tv/hooks/use-interval';
import getContentList from '@smart-tv/utils/kmm/getContentList';
import getContentSpecialList from '@smart-tv/utils/kmm/getContentSpecialList';
import getProviderCollectionContentList from '@smart-tv/utils/kmm/getProviderCollectionContentList';
import getUiElementContent from '@smart-tv/utils/kmm/getUiElementContent';

import useFavoritesLists from '../use-favorites-lists';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import { BaseError, networkError } from '@smart-tv/contexts/error-context/error-context';
import { rootStore } from '@smart-tv/components/stripes/_stores/store-root';
import useCollections from '../use-collections';
import { useAtomValue } from 'jotai';
import { pageAreaAtom } from '@smart-tv/atoms';

const DEFAULT_UPDATE_INTERVAL = 300000;

const SCROLL_FETCH_INCREMENT = 30;
const FAVORITES_STRIPE_VISIBLE_ITEMS = 8;

export enum UIElementContentSource {
  UIelements,
  UIelementsProvider,
  Provider,
  Universe,
  SpecialUniverse
}

export interface ProviderContentProps {
  itemLimit: number;
  mainGenreId: number;
  providerCollectionId?: number;
  sortingType?: UniverseSortingValue;
  subGenreId: number;
  universeId: number;
  start?: number;
}

export const useUiElementContent = (
  uiElementId: number,
  contentSource: UIElementContentSource,
  fetchAll: boolean,
  disablePagination = false,
  providerContentProps?: ProviderContentProps,
  style?: UIElementStyle,
  isLoaded?: boolean,
  initialContent?: UIElementContent
) => {
  const vodPlayerContext = useVodPlayer();
  const playerContext = usePlayerContext();

  const isPlayerActive = vodPlayerContext.isPlaying || playerContext.isPlaying;

  const [uiElementContent, setUiElementContent] = React.useState<UIElementContent | undefined>(
    initialContent
  );

  const loaderRef = useRef() as MutableRefObject<HTMLDivElement>;

  const [fetchFromIndex, setFetchFromIndex] = React.useState<number>(
    initialContent?.items.length ?? 0
  );

  const [maxCollectionIndex, setMaxCollectionIndex] = React.useState<number>(
    initialContent?.contentTotalCount ?? initialContent?.items.length ?? 0
  );

  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const [updateInterval, setUpdateInterval] = React.useState<number | undefined>(
    initialContent ? initialContent.updateNext * 1000 || DEFAULT_UPDATE_INTERVAL : undefined
  );

  const { selectedFavoritesList } = useFavoritesLists();

  const { handleClose } = useCollections();

  const pageAreaAtomValue = useAtomValue(pageAreaAtom);

  const [missedUpdate, setMissedUpdate] = React.useState<boolean>(false);

  const fetchUiElementContent = React.useCallback(async () => {
    // Return if the player is active, to avoid fetching content while the player is playing.

    if (isPlayerActive) {
      setMissedUpdate(true);
      return;
    }

    try {
      let uiElementContent = null;

      const itemLimit = fetchAll ? SCROLL_FETCH_INCREMENT : 9;

      if (providerContentProps) {
        providerContentProps.itemLimit = itemLimit;
        providerContentProps.start = 0;
      }

      // universe lists (except special lists)
      if (contentSource == UIElementContentSource.Universe && providerContentProps && style) {
        uiElementContent = await getContentList(
          providerContentProps.universeId,
          ContentListProviders.Subscribed,
          providerContentProps?.mainGenreId,
          providerContentProps?.subGenreId,
          style,
          itemLimit
        );

        setFetchFromIndex(SCROLL_FETCH_INCREMENT);
      }

      // normal uielements (frontpage lists & provider special list)
      if (contentSource === UIElementContentSource.UIelements || contentSource === undefined) {
        const response = await getUiElementContent(
          uiElementId,
          selectedFavoritesList?.id,
          pageAreaAtomValue
        );

        if (!response?.items || response.items.length === 0) {
          throw response;
        }

        setUiElementContent(response);
        setUpdateInterval(response.updateNext * 1000 || DEFAULT_UPDATE_INTERVAL);
        rootStore.stripesStore.updateUiElementContent(uiElementId, response);
      }
      if (contentSource === UIElementContentSource.Provider && providerContentProps && style) {
        uiElementContent = await getProviderCollectionContentList(providerContentProps, style);

        setFetchFromIndex(SCROLL_FETCH_INCREMENT);
      }

      // universe special lists
      if (
        contentSource === UIElementContentSource.SpecialUniverse &&
        providerContentProps &&
        style
      ) {
        uiElementContent = await getContentSpecialList(
          providerContentProps.universeId,
          ContentListProviders.Subscribed,
          providerContentProps?.mainGenreId,
          style,
          itemLimit
        );
      }

      uiElementContent && setUiElementContent(uiElementContent);

      setMaxCollectionIndex(uiElementContent?.contentTotalCount ?? 30);
      setFetchFromIndex(SCROLL_FETCH_INCREMENT);
    } catch (error) {
      if (error === networkError || (error as BaseError).offline) {
        rootStore.stripesStore.setError(networkError);
        handleClose();
      }
    }
  }, [
    isPlayerActive,
    fetchAll,
    providerContentProps,
    contentSource,
    style,
    uiElementId,
    selectedFavoritesList?.id,
    handleClose,
    pageAreaAtomValue
  ]);

  // Fetch the content on first mount
  useEffect(() => {
    if (fetchFromIndex === 0) fetchUiElementContent();
  }, [fetchFromIndex, fetchUiElementContent]);

  const fetchUiElementContentOnScroll = useCallback(async () => {
    if (!disablePagination && isFetching) return;

    try {
      if (fetchFromIndex < maxCollectionIndex && style && providerContentProps) {
        let uiElementContent = null;
        setIsFetching(true);

        if (contentSource === UIElementContentSource.Provider) {
          providerContentProps.start = fetchFromIndex;
          providerContentProps.itemLimit = SCROLL_FETCH_INCREMENT;

          uiElementContent = await getProviderCollectionContentList(providerContentProps, style);
        }

        // universe lists (except special lists)
        if (contentSource == UIElementContentSource.Universe) {
          providerContentProps.start = fetchFromIndex;

          uiElementContent = await getContentList(
            providerContentProps.universeId,
            ContentListProviders.Subscribed,
            providerContentProps?.mainGenreId,
            providerContentProps?.subGenreId,
            style,
            SCROLL_FETCH_INCREMENT,
            fetchFromIndex
          );
        }

        // universe special lists
        if (contentSource === UIElementContentSource.SpecialUniverse) {
          uiElementContent = await getContentSpecialList(
            providerContentProps.universeId,
            ContentListProviders.Subscribed,
            providerContentProps?.mainGenreId,
            style,
            SCROLL_FETCH_INCREMENT,
            fetchFromIndex
          );
        }

        uiElementContent &&
          setUiElementContent((prev) => {
            return {
              ...prev,
              ...uiElementContent,
              items: [...(prev?.items ?? []), ...uiElementContent.items]
            };
          });

        setFetchFromIndex(fetchFromIndex + SCROLL_FETCH_INCREMENT);
      }
    } catch (error) {
      if (error === networkError || (error as BaseError).offline) {
        rootStore.stripesStore.setError(networkError);
        handleClose();
      }
    } finally {
      setIsFetching(false);
    }
  }, [
    disablePagination,
    isFetching,
    fetchFromIndex,
    maxCollectionIndex,
    style,
    providerContentProps,
    contentSource,
    handleClose
  ]);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        fetchUiElementContentOnScroll();
      }
    });

    const loadingContainerRef = loaderRef.current;
    if (isLoaded && loaderRef && loadingContainerRef) {
      observer.observe(loadingContainerRef);
    }

    return () => {
      if (loaderRef && loadingContainerRef) {
        observer.unobserve(loadingContainerRef);
      }
    };
  }, [fetchUiElementContentOnScroll, isLoaded]);

  // Refetch the content on regular intervals
  useInterval(() => fetchUiElementContent(), updateInterval);

  React.useEffect(() => {
    if (missedUpdate && !isPlayerActive) {
      fetchUiElementContent();
      setMissedUpdate(false);
    }
  }, [missedUpdate, isPlayerActive, fetchUiElementContent, uiElementId]);

  // Refetch when stripes with update_on_favorite are updated
  React.useEffect(() => {
    if (rootStore.stripesStore.stripesToRefetch.has(uiElementId)) {
      fetchUiElementContent().finally(() => {
        // Clear this stripe from the refetch set
        rootStore.stripesStore.stripesToRefetch.delete(uiElementId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiElementId, rootStore.stripesStore.stripesToRefetch.size, fetchUiElementContent]);

  const handleFavoritesUpdate = useCallback((item: ContentItem, action: 'add' | 'remove') => {
    setUiElementContent((currentContent) => {
      if (!currentContent) return currentContent;

      let updatedItems = currentContent.items;

      if (action === 'add') {
        const itemAlreadyExists = currentContent.items.some(
          (existingItem) => existingItem.contentId === item.contentId
        );
        if (itemAlreadyExists) {
          return currentContent;
        }

        updatedItems = [item, ...currentContent.items];
      } else {
        updatedItems = currentContent.items.filter(
          (existingItem) => existingItem.contentId !== item.contentId
        );
      }

      const shouldShowAll = updatedItems.length > FAVORITES_STRIPE_VISIBLE_ITEMS;

      return {
        ...currentContent,
        items: updatedItems,
        contentTotalCount: updatedItems.length,
        showAllElementCell: {
          ...currentContent.showAllElementCell,
          show: shouldShowAll,
          position: FAVORITES_STRIPE_VISIBLE_ITEMS
        }
      };
    });
  }, []);

  return {
    uiElementContent,
    isFetching,
    loaderRef,
    handleFavoritesUpdate
  };
};

export default useUiElementContent;
